import { useState } from "react";
import { toast } from "sonner";
import { Layers3, Loader2, PackagePlus, Plus, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { CreateOrderPanel } from "@/components/panel/CreateOrderPanel";
import { DEPARTMENTS, SHIPPING_METHODS, STATUS_VALUES, needsReason } from "@/constants/workflow";
import { canEdit, useAuth } from "@/context/AuthContext";
import { useOffline } from "@/context/OfflineContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Left side panel: bulk status change + pallet assignment for the current selection. */
export function BulkActionsBar({ projectId, selection, pallets, panelVersions, onDone, onClose, onOrderCreated }) {
  const { capabilities, user } = useAuth();
  const { submit } = useOffline();
  const [busy, setBusy] = useState(false);
  const [department, setDepartment] = useState("");
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [shippingMethod, setShippingMethod] = useState("");
  const [shipmentCode, setShipmentCode] = useState("");
  const [shippedOn, setShippedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [arrivalDate, setArrivalDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [installedOn, setInstalledOn] = useState("");
  const [palletId, setPalletId] = useState("");
  const [newPalletCode, setNewPalletCode] = useState("");
  const [creatingPallet, setCreatingPallet] = useState(false);

  const palletAllowed =
    capabilities?.is_admin ||
    (capabilities?.data_editors?.pallet || []).some((r) => (user?.roles || []).includes(r));

  const isShipping = department === "logistics" && status === "Shipped";
  const isArrival = department === "logistics" && status === "Arrived";
  const isInstall = department === "site" && status === "Installed";
  const shippingReady = Boolean(shippingMethod && shipmentCode.trim() && shippedOn && palletId);
  const reasonRequired = needsReason(department, status);

  const createPallet = async () => {
    const code = newPalletCode.trim();
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/pallets`, { code });
      setPalletId(data.id);
      setNewPalletCode("");
      setCreatingPallet(false);
      toast.success(`Pallet ${data.code} created`);
      onDone?.({ keepSelection: true });
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  const applyStatus = async () => {
    if (isShipping && !shippingReady) {
      toast.error("Shipping method, shipment code, shipping date and pallet are required");
      return;
    }
    if (isArrival && !arrivalDate) {
      toast.error("An arrival date is required");
      return;
    }
    if (isInstall && !installedOn) {
      toast.error("An installation date is required");
      return;
    }
    if (reasonRequired && !reason.trim()) {
      toast.error(`An explanation is required when setting ${department.toUpperCase()} to ${status}`);
      return;
    }
    setBusy(true);
    try {
      const body = {
        panel_uids: selection,
        department,
        status: status === "__null__" ? null : status,
        reason: reason.trim() || null,
        arrival_date: isArrival ? arrivalDate : null,
        installed_on: isInstall ? installedOn : null,
      };
      // Shipping needs the server response to chain pallet/shipment writes, so it stays online-only.
      if (!isShipping) {
        const result = await submit({
          method: "put",
          path: `/projects/${projectId}/panels/status/bulk`,
          body,
          label: `${selection.length} panel(s): ${department.toUpperCase()} → ${status}`,
          panel_uid: selection.length === 1 ? selection[0] : null,
          version: selection.length === 1 ? panelVersions?.[selection[0]] || null : null,
        });
        if (!result.ok) {
          toast.error(result.error);
          return;
        }
        if (result.queued) {
          toast.success(`Queued offline — ${department.toUpperCase()} → ${status}`);
          setReason("");
          setStatus("");
          return;
        }
        const failed = result.data.failed || [];
        if (failed.length)
          toast.warning(`${result.data.updated.length} updated · ${failed.length} blocked`, {
            description: failed[0]?.detail,
          });
        else toast.success(`${result.data.updated.length} panels updated`);
        setReason("");
        setStatus("");
        onDone?.();
        return;
      }
      const { data } = await api.put(`/projects/${projectId}/panels/status/bulk`, body);
      const okUids = (data.updated || []).map((row) => row.panel_uid).filter(Boolean);
      if (isShipping && okUids.length) {
        await api.put(`/projects/${projectId}/panels/shipping/bulk`, {
          panel_uids: okUids,
          shipping_method: shippingMethod,
          shipment_code: shipmentCode.trim(),
          shipped_on: shippedOn,
        });
        await api.put(`/projects/${projectId}/pallets/assign`, {
          pallet_id: palletId,
          panel_uids: okUids,
        });
      }
      if (data.failed.length)
        toast.warning(`${data.updated.length} updated · ${data.failed.length} blocked`, {
          description: data.failed[0]?.detail,
        });
      else toast.success(`${data.updated.length} panels updated`);
      setReason("");
      setShippingMethod("");
      setShipmentCode("");
      setShippedOn(new Date().toISOString().slice(0, 10));
      setPalletId("");
      setStatus("");
      onDone?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  const assign = async (value) => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/pallets/assign`, {
        pallet_id: value === "__none__" ? null : value,
        panel_uids: selection,
      });
      toast.success(`Pallet assigned to ${selection.length} panels`);
      onDone?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  // Production is read-only here — it is driven by the Factory workspace.
  const departments = DEPARTMENTS.filter(
    (d) => d.key !== "production" && canEdit(capabilities, d.key)
  );
  const isProcurement =
    !capabilities?.is_guest &&
    ((user?.roles || []).includes("procurement") || capabilities?.is_admin);
  const canChangeStatus = departments.length > 0;
  const [mode, setMode] = useState(
    isProcurement && !canChangeStatus ? "order" : "status"
  );
  const activeMode = isProcurement ? (canChangeStatus ? mode : "order") : "status";

  return (
    <aside
      data-testid="bulk-actions-bar"
      className="okn-scroll absolute left-0 top-0 z-30 flex h-full w-[300px] max-w-full flex-col overflow-y-auto border-r border-slate-200 bg-white/97 backdrop-blur-md max-lg:inset-x-0 max-lg:bottom-0 max-lg:top-auto max-lg:h-[68vh] max-lg:w-full max-lg:rounded-t-2xl max-lg:border-r-0 max-lg:border-t max-lg:bg-white max-lg:shadow-2xl"
    >
      <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-4">
        <span
          data-testid="bulk-selection-count"
          className="mono flex min-w-0 flex-col gap-0.5 text-cyan-700"
        >
          <span className="flex items-center gap-1.5 text-[12.5px] font-medium uppercase tracking-wider">
            {activeMode === "order" ? (
              <>
                <PackagePlus size={14} /> Create Order
              </>
            ) : (
              <>
                <Layers3 size={14} /> Change Status
              </>
            )}
          </span>
          <span className="text-[10.5px] uppercase tracking-wider text-slate-500">
            {selection.length} selected
          </span>
        </span>
        <button
          data-testid="close-bulk-panel-button"
          onClick={onClose}
          className="okn-tool grid h-7 w-7 place-items-center rounded-md text-slate-600 hover:bg-slate-100"
        >
          <X size={15} />
        </button>
      </div>

      {isProcurement && canChangeStatus ? (
        <div className="flex items-center gap-1 border-b border-slate-200 bg-slate-50 px-3 py-2">
          {[
            { key: "status", label: "Change Status" },
            { key: "order", label: "Create Order" },
          ].map((tab) => (
            <button
              key={tab.key}
              data-testid={`bulk-mode-${tab.key}`}
              onClick={() => setMode(tab.key)}
              className={`mono flex-1 rounded-md px-2 py-1 text-[9.5px] uppercase tracking-wider transition-colors ${
                activeMode === tab.key
                  ? "bg-white text-cyan-700 shadow-sm ring-1 ring-cyan-200"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : null}

      {activeMode === "order" ? (
        <CreateOrderPanel projectId={projectId} selection={selection} onCreated={onOrderCreated} />
      ) : (
      <div className="space-y-4 px-4 py-4">
        <div>
          <p className="mono mb-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Department
          </p>
          <Select
            value={department}
            onValueChange={(value) => {
              setDepartment(value);
              setStatus("");
              setShippingMethod("");
              setShipmentCode("");
              setPalletId("");
            }}
          >
            <SelectTrigger
              data-testid="bulk-department-select"
              className="h-9 border-slate-200 bg-[#EEF2F8] text-xs"
            >
              <SelectValue placeholder="Select department" />
            </SelectTrigger>
            <SelectContent className="border-slate-200 bg-white text-slate-800">
              {departments.map((dept) => (
                <SelectItem key={dept.key} value={dept.key} className="text-xs">
                  {dept.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {department ? (
          <div>
            <p className="mono mb-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500">
              New status
            </p>
            <Select
              value={status}
              onValueChange={(value) => {
                setStatus(value);
                if (value !== "Shipped") {
                  setShippingMethod("");
                  setShipmentCode("");
                  setPalletId("");
                }
              }}
            >
              <SelectTrigger
                data-testid="bulk-status-select"
                className="h-9 border-slate-200 bg-[#EEF2F8] text-xs"
              >
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                {STATUS_VALUES[department].map((value) => (
                  <SelectItem key={value} value={value} className="text-xs">
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        {isArrival ? (
          <div
            className="space-y-2 rounded-lg border border-slate-200 bg-[#F8FAFC] p-3"
            data-testid="bulk-arrival-block"
          >
            <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Arrival Date <span className="text-red-600">*</span>
            </p>
            <input
              data-testid="bulk-arrival-date-input"
              type="date"
              value={arrivalDate}
              onChange={(e) => setArrivalDate(e.target.value)}
              className="mono h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
            />
          </div>
        ) : null}

        {isInstall ? (
          <div
            className="space-y-2 rounded-lg border border-slate-200 bg-[#F8FAFC] p-3"
            data-testid="bulk-install-block"
          >
            <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Installation Date <span className="text-red-600">*</span>
            </p>
            <p className="text-[10.5px] text-slate-500">
              The real installation day, not today&apos;s date.
            </p>
            <input
              data-testid="bulk-install-date-input"
              type="date"
              value={installedOn}
              onChange={(e) => setInstalledOn(e.target.value)}
              className="mono h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
            />
          </div>
        ) : null}

        {isShipping ? (
          <div className="space-y-2 rounded-lg border border-slate-200 bg-[#F8FAFC] p-3" data-testid="bulk-shipping-block">
            <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Shipping Method <span className="text-red-600">*</span>
            </p>
            <Select value={shippingMethod} onValueChange={setShippingMethod} disabled={busy}>
              <SelectTrigger
                data-testid="bulk-shipping-method-select"
                className="h-9 border-slate-200 bg-white text-xs"
              >
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                {SHIPPING_METHODS.map((method) => (
                  <SelectItem
                    key={method}
                    value={method}
                    data-testid={`bulk-shipping-method-${method.replace(/\s/g, "-")}`}
                    className="text-xs"
                  >
                    {method}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {shippingMethod ? (
              <>
                <p className="mono pt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  Shipment Code <span className="text-red-600">*</span>
                </p>
                <input
                  data-testid="bulk-shipment-code-input"
                  value={shipmentCode}
                  onChange={(e) => setShipmentCode(e.target.value)}
                  placeholder="e.g. SHP-001"
                  className="mono h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                />

                <p className="mono pt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  Shipping Date <span className="text-red-600">*</span>
                </p>
                <input
                  data-testid="bulk-shipped-on-input"
                  type="date"
                  value={shippedOn}
                  onChange={(e) => setShippedOn(e.target.value)}
                  className="mono h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                />

                <p className="mono pt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  Pallet <span className="text-red-600">*</span>
                </p>
                <Select value={palletId} onValueChange={setPalletId} disabled={busy}>
                  <SelectTrigger
                    data-testid="bulk-shipping-pallet-select"
                    className="h-9 border-slate-200 bg-white text-xs"
                  >
                    <SelectValue placeholder="Select pallet" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-200 bg-white text-slate-800">
                    {pallets.map((pallet) => (
                      <SelectItem key={pallet.id} value={pallet.id} className="mono text-xs">
                        {pallet.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {creatingPallet ? (
                  <div className="flex gap-1.5">
                    <input
                      data-testid="bulk-new-pallet-input"
                      value={newPalletCode}
                      onChange={(e) => setNewPalletCode(e.target.value)}
                      placeholder="New pallet code"
                      className="mono h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                    />
                    <Button
                      data-testid="bulk-create-pallet-confirm-button"
                      onClick={createPallet}
                      disabled={busy || !newPalletCode.trim()}
                      className="h-8 shrink-0 rounded-md bg-slate-800 px-2 text-[10px] uppercase text-white hover:bg-slate-700"
                    >
                      Add
                    </Button>
                  </div>
                ) : (
                  <button
                    data-testid="bulk-create-pallet-button"
                    onClick={() => setCreatingPallet(true)}
                    className="flex items-center gap-1 text-[11px] text-cyan-700 hover:text-cyan-600"
                  >
                    <Plus size={12} /> New pallet
                  </button>
                )}

                <p className="text-[10px] leading-relaxed text-slate-500">
                  ETA is calculated automatically from the shipping date: By Truck +10 days, By
                  Container +24 days.
                </p>
              </>
            ) : null}
          </div>
        ) : null}

        {status ? (
          <>
            <Textarea
              data-testid="bulk-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                reasonRequired
                  ? `Explanation required for ${status} — describe why`
                  : "Reason (optional)"
              }
              className={`min-h-16 bg-[#F8FAFC] text-xs ${
                reasonRequired && !reason.trim() ? "border-red-400" : "border-slate-200"
              }`}
            />
            {reasonRequired && !reason.trim() ? (
              <p data-testid="bulk-reason-warning" className="text-[11px] text-red-600">
                An explanation is mandatory when setting {department.toUpperCase()} to {status}.
              </p>
            ) : null}
            <Button
              data-testid="bulk-apply-status-button"
              onClick={applyStatus}
              disabled={
                busy ||
                (isShipping && !shippingReady) ||
                (isArrival && !arrivalDate) ||
                (isInstall && !installedOn) ||
                (reasonRequired && !reason.trim())
              }
              className="h-9 w-full rounded-full bg-cyan-600 text-[11px] uppercase tracking-wider text-white hover:bg-cyan-500 disabled:opacity-40"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : `Apply to ${selection.length} panels`}
            </Button>
          </>
        ) : null}

        {palletAllowed && !isShipping && department !== "logistics" ? (
          <div className="border-t border-slate-200 pt-4">
            <p className="mono mb-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Pallet
            </p>
            <Select onValueChange={assign} disabled={busy}>
              <SelectTrigger
                data-testid="bulk-pallet-select"
                className="h-9 border-slate-200 bg-[#EEF2F8] text-xs"
              >
                <SelectValue placeholder="Assign pallet" />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                <SelectItem value="__none__" className="text-xs">
                  — remove pallet —
                </SelectItem>
                {pallets.map((pallet) => (
                  <SelectItem key={pallet.id} value={pallet.id} className="mono text-xs">
                    {pallet.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>
      )}
    </aside>
  );
}
